import { NewbuildingCarousel } from '@cian/frontend-newbuilding-carousel-component';
import { useBooleanState, useIntersectionObserver, useIsomorphicLayoutEffect } from '@cian/react-utils';

import { type FC, useCallback, useRef, useState } from 'react';

import { type MainCarouselBanner } from 'shared/types/mainCarousel';

import { Layout } from '../Layout';
import { NoImage } from '../NoImage';
import { type AnimationDuration, SliderProgressBar } from '../SliderProgressBar';
import { firstPaintOptions, mediaSettings } from '../Carousel/config';

import { CarouselMainV2Slide } from './internal/components/CarouselMainV2Slide';

import styles from './CarouselMainV2.css';

type CarouselMainV2Props = {
  banners: MainCarouselBanner[];
  interval: AnimationDuration;
  regionId: number;
  testId?: string;
  onDotClick: (index: number) => void;
  onSelectApartmentClick: (newbuildingId: number, index: number) => void;
  onView: () => void;
};

export const CarouselMainV2: FC<CarouselMainV2Props> = ({
  banners,
  interval,
  regionId,
  testId,
  onDotClick,
  onSelectApartmentClick,
  onView,
}) => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const { state: paused, setFalse: setNotPaused, setTrue: setPaused } = useBooleanState(false);
  const carouselRef = useRef<HTMLElement>(null);
  const currentRegionIdRef = useRef(regionId);
  const activeSlide = currentRegionIdRef.current === regionId ? currentSlide : 1;

  const { subscribeState } = useIntersectionObserver({
    deps: [onView],
    elementRef: carouselRef,
    observerOptions: { threshold: 0.5 },
    onInViewport: ({ subscribeState: currentSubscribeState }) => {
      currentSubscribeState.setSubscribeUnavailable();
      onView();
    },
  });

  useIsomorphicLayoutEffect(() => {
    currentRegionIdRef.current = regionId;
    setCurrentSlide(1);
    setNotPaused();
    subscribeState.setSubscribeAvailable();
  }, [regionId, setNotPaused, subscribeState]);

  const handleAnimationEnd = useCallback(
    () => setCurrentSlide(prev => (prev < banners.length ? prev + 1 : 1)),
    [banners.length],
  );

  if (!banners.length) {
    return (
      <section className={styles['carousel']} ref={carouselRef}>
        <NoImage />
      </section>
    );
  }

  return (
    <section className={styles['carousel']} data-testid={testId} ref={carouselRef}>
      <div className={styles['slides']}>
        <NewbuildingCarousel
          defaultCurrentSlide={activeSlide}
          firstPaintOptions={firstPaintOptions}
          media={mediaSettings}
          slides={banners.map((banner, index) => (
            <CarouselMainV2Slide
              active={index === activeSlide - 1}
              banner={banner}
              index={index}
              key={banner.id}
              testId={`main-carousel-slide-${banner.id}`}
              onSelectApartmentClick={onSelectApartmentClick}
              onTickerMouseLeave={setNotPaused}
              onTickerMouseOver={setPaused}
            />
          ))}
          onSlideChange={({ currentSlide: slideIndex }) => setCurrentSlide(slideIndex)}
        />
      </div>

      {banners.length > 1 && (
        <div className={styles['progress-bar']}>
          <Layout>
            <div className={styles['progress-bar__wrapper']}>
              <SliderProgressBar
                activeIndex={activeSlide - 1}
                count={banners.length}
                duration={interval}
                paused={paused}
                onAnimationEnd={handleAnimationEnd}
                onDotClick={index => {
                  onDotClick(index);
                  setCurrentSlide(index + 1);
                }}
                onProgressBarHover={setPaused}
                onProgressBarLeave={setNotPaused}
              />
            </div>
          </Layout>
        </div>
      )}
    </section>
  );
};
